import { useEffect, useRef, useState } from "react";
import { createChart, CandlestickSeries, HistogramSeries, LineSeries, Time } from "lightweight-charts";

interface ChartData {
  time: Time;
  open: number;
  high: number;
  low: number;
  close: number;
}

interface VolumeData {
  time: Time;
  value: number;
  color: string;
}

// Opportunity types
interface Opportunity {
  id: string;
  name: string;
  type: "bullish" | "bearish" | "neutral";
  strength: "strong" | "moderate" | "weak";
  description: string;
  icon: string;
}

// Fetch OHLCV data from CoinGecko
async function fetchOHLCV(symbol: string, days = 90): Promise<ChartData[]> {
  const coinMap: Record<string, string> = {
    "BTC/USDT": "bitcoin",
    "ETH/USDT": "ethereum", 
    "SOL/USDT": "solana",
    "BNB/USDT": "binancecoin",
    "XRP/USDT": "ripple",
    "ADA/USDT": "cardano",
    "DOGE/USDT": "dogecoin",
    "AVAX/USDT": "avalanche-2",
    "DOT/USDT": "polkadot",
    "LINK/USDT": "chainlink",
    "MSTR": "microstrategy",
    "NVDA": "nvidia",
    "AMD": "advanced-micro-devices",
    "AAPL": "apple",
    "MSFT": "microsoft",
    "GOOGL": "google",
    "AMZN": "amazon",
    "TSLA": "tesla",
    "META": "meta",
    "TLT": "ishares-20-plus-year-treasury-bond-etf",
  };
  
  // Stock symbols that need different handling (using Yahoo Finance)
  const stockSymbols = ["MSTR", "NVDA", "AMD", "AAPL", "MSFT", "GOOGL", "AMZN", "TSLA", "META", "TLT"];
  const isStock = stockSymbols.includes(symbol);
  
  // For stocks/ETFs like TLT, use Yahoo Finance
  if (isStock && symbol !== "TLT") {
    // For crypto stocks, try to find them on CoinGecko first
    const coin = coinMap[symbol];
    if (coin) {
      try {
        const response = await fetch(
          `https://api.coingecko.com/api/v3/coins/${coin}/ohlc?vs_currency=usd&days=${days}`
        );
        const data = await response.json();
        if (!Array.isArray(data)) return [];
        return data.map((item: number[]) => ({
          time: Math.floor(item[0] / 1000) as Time,
          open: item[1],
          high: item[2],
          low: item[3],
          close: item[4],
        }));
      } catch (e) {
        console.error("CoinGecko fetch error:", e);
      }
    }
    return [];
  }
  
  // For TLT and other ETFs, try Yahoo Finance
  if (symbol === "TLT") {
    try {
      // Use Yahoo Finance via a proxy or direct API
      const ticker = "TLT";
      const endTimestamp = Math.floor(Date.now() / 1000);
      const startTimestamp = endTimestamp - (days * 24 * 60 * 60);
      
      // Using a public Yahoo Finance proxy
      const response = await fetch(
        `https://query1.finance.yahoo.com/v8/finance/chart/${ticker}?period1=${startTimestamp}&period2=${endTimestamp}&interval=1d`
      );
      const json = await response.json();
      
      if (json?.chart?.result?.[0]) {
        const result = json.chart.result[0];
        const timestamps = result.timestamp || [];
        const quotes = result.indicators?.quote?.[0] || {};
        
        return timestamps.map((ts: number, i: number) => ({
          time: ts as Time,
          open: quotes.open?.[i] || 0,
          high: quotes.high?.[i] || 0,
          low: quotes.low?.[i] || 0,
          close: quotes.close?.[i] || 0,
        })).filter((d: ChartData) => d.close > 0);
      }
    } catch (e) {
      console.error("Yahoo Finance fetch error:", e);
    }
    return [];
  }
  
  const coin = coinMap[symbol] || "bitcoin";
  
  try {
    const response = await fetch(
      `https://api.coingecko.com/api/v3/coins/${coin}/ohlc?vs_currency=usd&days=${days}`
    );
    const data = await response.json();
    
    if (!Array.isArray(data)) return [];
    
    return data.map((item: number[]) => ({
      time: Math.floor(item[0] / 1000) as Time,
      open: item[1],
      high: item[2],
      low: item[3],
      close: item[4],
    }));
  } catch (error) {
    console.error("Error fetching OHLCV:", error);
    return [];
  }
}

// Calculate SMA indicator
function calculateSMA(data: ChartData[], period: number): { time: Time; value: number }[] {
  const sma: { time: Time; value: number }[] = [];
  
  for (let i = period - 1; i < data.length; i++) {
    let sum = 0;
    for (let j = 0; j < period; j++) {
      sum += data[i - j].close;
    }
    sma.push({
      time: data[i].time,
      value: sum / period,
    });
  }
  
  return sma;
}

// Calculate EMA indicator
function calculateEMA(data: ChartData[], period: number): { time: Time; value: number }[] {
  const ema: { time: Time; value: number }[] = [];
  const k = 2 / (period + 1);
  
  let sum = 0;
  for (let i = 0; i < period; i++) {
    sum += data[i].close;
  }
  let prevEma = sum / period;
  ema.push({ time: data[period - 1].time, value: prevEma });
  
  for (let i = period; i < data.length; i++) {
    const currentEma = data[i].close * k + prevEma * (1 - k);
    ema.push({ time: data[i].time, value: currentEma });
    prevEma = currentEma;
  }
  
  return ema;
}

// Calculate RSI indicator
function calculateRSI(data: ChartData[], period = 14): { time: Time; value: number }[] | null {
  if (data.length < period + 1) return null;
  
  const rsi: { time: Time; value: number }[] = [];
  const gains: number[] = [];
  const losses: number[] = [];
  
  for (let i = 1; i < data.length; i++) {
    const change = data[i].close - data[i - 1].close;
    gains.push(change > 0 ? change : 0);
    losses.push(change < 0 ? Math.abs(change) : 0);
  }
  
  for (let i = period; i < gains.length; i++) {
    const avgGain = gains.slice(i - period, i).reduce((a, b) => a + b, 0) / period;
    const avgLoss = losses.slice(i - period, i).reduce((a, b) => a + b, 0) / period;
    const rs = avgLoss === 0 ? 100 : avgGain / avgLoss;
    const rsiValue = 100 - (100 / (1 + rs));
    
    rsi.push({
      time: data[i + 1].time,
      value: rsiValue,
    });
  }
  
  return rsi;
}

// Calculate Bollinger Bands
function calculateBB(data: ChartData[], period = 20): { upper: { time: Time; value: number }; middle: { time: Time; value: number }; lower: { time: Time; value: number } }[] {
  const bb: { upper: { time: Time; value: number }; middle: { time: Time; value: number }; lower: { time: Time; value: number } }[] = [];
  
  for (let i = period - 1; i < data.length; i++) {
    const slice = data.slice(i - period + 1, i + 1);
    const sum = slice.reduce((a, b) => a + b.close, 0);
    const middle = sum / period;
    
    const squaredDiffs = slice.map(d => Math.pow(d.close - middle, 2));
    const variance = squaredDiffs.reduce((a, b) => a + b, 0) / period;
    const stdDev = Math.sqrt(variance);
    
    bb.push({
      upper: { time: data[i].time, value: middle + 2 * stdDev },
      middle: { time: data[i].time, value: middle },
      lower: { time: data[i].time, value: middle - 2 * stdDev },
    });
  }
  
  return bb;
}

// Calculate MACD
function calculateMACD(data: ChartData[]): { macd: { time: Time; value: number }; signal: { time: Time; value: number }; histogram: { time: Time; value: number } }[] | null {
  const ema12 = calculateEMA(data, 12);
  const ema26 = calculateEMA(data, 26);
  
  if (ema12.length < 9 || ema26.length < 9) return null;
  
  const macdLine: { time: Time; value: number }[] = [];
  const startIdx = ema26.length - ema12.length;
  
  for (let i = 0; i < ema12.length; i++) {
    const idx = startIdx + i;
    if (idx >= 0 && idx < ema26.length) {
      macdLine.push({
        time: ema12[i].time,
        value: ema12[i].value - ema26[idx].value,
      });
    }
  }
  
  const signalLine: { time: Time; value: number }[] = [];
  const k = 2 / (10);
  let prevSignal = macdLine.slice(0, 9).reduce((a, b) => a + b.value, 0) / 9;
  
  for (let i = 0; i < macdLine.length; i++) {
    if (i < 9) {
      signalLine.push({ time: macdLine[i].time, value: prevSignal });
    } else {
      const signal = macdLine[i].value * k + prevSignal * (1 - k);
      signalLine.push({ time: macdLine[i].time, value: signal });
      prevSignal = signal;
    }
  }
  
  const histogram: { time: Time; value: number }[] = [];
  for (let i = 0; i < macdLine.length; i++) {
    histogram.push({
      time: macdLine[i].time,
      value: macdLine[i].value - signalLine[i].value,
    });
  }
  
  return macdLine.map((m, i) => ({
    macd: m,
    signal: signalLine[i],
    histogram: histogram[i],
  }));
}

// Scan for opportunities
function scanOpportunities(data: ChartData[]): Opportunity[] {
  const opportunities: Opportunity[] = [];
  
  if (data.length < 50) return opportunities;
  
  const currentPrice = data[data.length - 1].close;
  const prices = data.map(d => d.close);
  
  // 1. EMA Crossover (Golden Cross / Death Cross)
  const ema9 = calculateEMA(data, 9);
  const ema21 = calculateEMA(data, 21);
  const ema50 = calculateSMA(data, 50);
  
  if (ema9.length > 1 && ema21.length > 1) {
    const prevEma9 = ema9[ema9.length - 2].value;
    const prevEma21 = ema21[ema21.length - 2].value;
    const currEma9 = ema9[ema9.length - 1].value;
    const currEma21 = ema21[ema21.length - 1].value;
    
    if (prevEma9 <= prevEma21 && currEma9 > currEma21) {
      opportunities.push({
        id: "golden-cross",
        name: "Golden Cross",
        type: "bullish",
        strength: "strong",
        description: "EMA 9 crossed above EMA 21 - bullish signal",
        icon: "✨",
      });
    } else if (prevEma9 >= prevEma21 && currEma9 < currEma21) {
      opportunities.push({
        id: "death-cross",
        name: "Death Cross",
        type: "bearish",
        strength: "strong",
        description: "EMA 9 crossed below EMA 21 - bearish signal",
        icon: "💀",
      });
    }
  }
  
  // 2. RSI Oversold/Overbought
  const rsiData = calculateRSI(data, 14);
  if (rsiData && rsiData.length > 0) {
    const rsi = rsiData[rsiData.length - 1].value;
    if (rsi < 30) {
      opportunities.push({
        id: "rsi-oversold",
        name: "RSI Oversold",
        type: "bullish",
        strength: "moderate",
        description: `RSI at ${rsi.toFixed(1)} - potential bounce`,
        icon: "🔔",
      });
    } else if (rsi > 70) {
      opportunities.push({
        id: "rsi-overbought",
        name: "RSI Overbought",
        type: "bearish",
        strength: "moderate",
        description: `RSI at ${rsi.toFixed(1)} - potential pullback`,
        icon: "⚠️",
      });
    } else if (rsi < 40) {
      opportunities.push({
        id: "rsi-low",
        name: "RSI Low",
        type: "bullish",
        strength: "weak",
        description: `RSI at ${rsi.toFixed(1)} - approaching oversold`,
        icon: "📉",
      });
    }
  }
  
  // 3. Bollinger Band Squeeze
  const bbData = calculateBB(data, 20);
  if (bbData.length > 0) {
    const bb = bbData[bbData.length - 1];
    const position = (currentPrice - bb.lower.value) / (bb.upper.value - bb.lower.value);
    const width = (bb.upper.value - bb.lower.value) / bb.middle.value;
    
    if (width < 0.03) {
      opportunities.push({
        id: "bb-squeeze",
        name: "BB Squeeze",
        type: "neutral",
        strength: "strong",
        description: "Low volatility - breakout imminent",
        icon: "🔥",
      });
    }
    
    if (position < 0.15) {
      opportunities.push({
        id: "bb-lower",
        name: "Near Lower BB",
        type: "bullish",
        strength: "moderate",
        description: "Price near support - potential bounce",
        icon: "🟢",
      });
    } else if (position > 0.85) {
      opportunities.push({
        id: "bb-upper",
        name: "Near Upper BB",
        type: "bearish",
        strength: "moderate",
        description: "Price near resistance - may revert",
        icon: "🔴",
      });
    }
  }
  
  // 4. MACD Crossover
  const macdData = calculateMACD(data);
  if (macdData && macdData.length > 1) {
    const curr = macdData[macdData.length - 1];
    const prev = macdData[macdData.length - 2];
    
    if (prev.histogram.value <= 0 && curr.histogram.value > 0) {
      opportunities.push({
        id: "macd-bullish",
        name: "MACD Bullish Cross",
        type: "bullish",
        strength: "moderate",
        description: "MACD histogram turned positive",
        icon: "📈",
      });
    } else if (prev.histogram.value >= 0 && curr.histogram.value < 0) {
      opportunities.push({
        id: "macd-bearish",
        name: "MACD Bearish Cross",
        type: "bearish",
        strength: "moderate",
        description: "MACD histogram turned negative",
        icon: "📉",
      });
    }
  }
  
  // 5. Trend Analysis (Higher Highs / Lower Lows)
  const highs = data.map(d => d.high);
  const lows = data.map(d => d.low);
  const recentHighs = highs.slice(-5);
  const recentLows = lows.slice(-5);
  
  if (recentHighs[4] > recentHighs[0] && recentLows[4] > recentLows[0]) {
    opportunities.push({
      id: "uptrend",
      name: "Uptrend (HH/HL)",
      type: "bullish",
      strength: "moderate",
      description: "Higher highs and higher lows",
      icon: "🚀",
    });
  } else if (recentHighs[4] < recentHighs[0] && recentLows[4] < recentLows[0]) {
    opportunities.push({
      id: "downtrend",
      name: "Downtrend (LH/LL)",
      type: "bearish",
      strength: "moderate",
      description: "Lower highs and lower lows",
        icon: "🔻",
    });
  }
  
  // 6. Strong Move Detection
  const change7d = ((prices[prices.length - 1] - prices[Math.max(0, prices.length - 7)]) / prices[Math.max(0, prices.length - 7)]) * 100;
  const change30d = ((prices[prices.length - 1] - prices[Math.max(0, prices.length - 30)]) / prices[Math.max(0, prices.length - 30)]) * 100;
  
  if (change7d > 10) {
    opportunities.push({
      id: "strong-up-7d",
      name: "Strong Move Up (7D)",
      type: "neutral",
      strength: "moderate",
      description: `+${change7d.toFixed(1)}% in 7 days - watch for pullback`,
      icon: "⚡",
    });
  } else if (change7d < -10) {
    opportunities.push({
      id: "strong-down-7d",
      name: "Strong Move Down (7D)",
      type: "neutral",
      strength: "moderate",
      description: `${change7d.toFixed(1)}% in 7 days - potential bounce`,
      icon: "💨",
    });
  }
  
  // 7. Support/Resistance bounce
  const nearSupport = bbData.length > 0 && currentPrice < bbData[bbData.length - 1].middle.value * 1.02;
  const nearResistance = bbData.length > 0 && currentPrice > bbData[bbData.length - 1].middle.value * 0.98;
  
  if (ema50.length > 0) {
    const sma50 = ema50[ema50.length - 1].value;
    if (currentPrice > sma50 * 1.02) {
      opportunities.push({
        id: "above-sma50",
        name: "Above SMA50",
        type: "bullish",
        strength: "moderate",
        description: "Price holding above 50-day MA",
        icon: "✅",
      });
    } else if (currentPrice < sma50 * 0.98) {
      opportunities.push({
        id: "below-sma50",
        name: "Below SMA50",
        type: "bearish",
        strength: "moderate",
        description: "Price below 50-day MA",
        icon: "❌",
      });
    }
  }
  
  return opportunities;
}

// AI Analysis based on indicators
interface AIAnalysis {
  recommendation: "BUY" | "SELL" | "NEUTRAL";
  confidence: number;
  reasons: string[];
}

function analyzeWithAI(data: ChartData[]): AIAnalysis {
  if (data.length < 50) {
    return { recommendation: "NEUTRAL", confidence: 0, reasons: ["Insufficient data for analysis"] };
  }

  const reasons: string[] = [];
  let buyScore = 0;
  let sellScore = 0;

  const currentPrice = data[data.length - 1].close;
  const prices = data.map(d => d.close);

  const rsiData = calculateRSI(data, 14);
  if (rsiData && rsiData.length > 0) {
    const rsi = rsiData[rsiData.length - 1].value;
    if (rsi < 30) {
      buyScore += 2;
      reasons.push(`RSI oversold (${rsi.toFixed(1)})`);
    } else if (rsi > 70) {
      sellScore += 2;
      reasons.push(`RSI overbought (${rsi.toFixed(1)})`);
    } else if (rsi < 45) {
      buyScore += 1;
    } else if (rsi > 55) {
      sellScore += 1;
    }
  }

  const sma20 = calculateSMA(data, 20);
  const sma50 = calculateSMA(data, 50);
  
  if (sma20.length > 0 && sma50.length > 0) {
    const currentSma20 = sma20[sma20.length - 1].value;
    const currentSma50 = sma50[sma50.length - 1].value;
    
    if (currentPrice > currentSma20) buyScore += 1;
    else sellScore += 1;
    
    if (currentSma20 > currentSma50) {
      buyScore += 1;
      reasons.push("SMA20 > SMA50");
    } else {
      sellScore += 1;
      reasons.push("SMA20 < SMA50");
    }
  }

  const bbData = calculateBB(data, 20);
  if (bbData.length > 0) {
    const bb = bbData[bbData.length - 1];
    const position = (currentPrice - bb.lower.value) / (bb.upper.value - bb.lower.value);
    
    if (position < 0.2) {
      buyScore += 2;
      reasons.push("Near lower BB");
    } else if (position > 0.8) {
      sellScore += 2;
      reasons.push("Near upper BB");
    }
    
    const width = (bb.upper.value - bb.lower.value) / bb.middle.value;
    if (width < 0.03) {
      reasons.push("BB squeeze");
    }
  }

  const ema9 = calculateEMA(data, 9);
  const ema21 = calculateEMA(data, 21);
  
  if (ema9.length > 1 && ema21.length > 1) {
    const prevEma9 = ema9[ema9.length - 2].value;
    const prevEma21 = ema21[ema21.length - 2].value;
    const currEma9 = ema9[ema9.length - 1].value;
    const currEma21 = ema21[ema21.length - 1].value;
    
    if (prevEma9 <= prevEma21 && currEma9 > currEma21) {
      buyScore += 2;
      reasons.push("Golden Cross");
    } else if (prevEma9 >= prevEma21 && currEma9 < currEma21) {
      sellScore += 2;
      reasons.push("Death Cross");
    }
  }

  const macdData = calculateMACD(data);
  if (macdData && macdData.length > 0) {
    const macd = macdData[macdData.length - 1];
    if (macd.histogram.value > 0) buyScore += 1;
    else sellScore += 1;
  }

  const totalScore = buyScore + sellScore;
  const confidence = totalScore > 0 ? Math.abs(buyScore - sellScore) / totalScore * 100 : 0;

  let recommendation: "BUY" | "SELL" | "NEUTRAL";
  if (buyScore > sellScore + 1) recommendation = "BUY";
  else if (sellScore > buyScore + 1) recommendation = "SELL";
  else recommendation = "NEUTRAL";

  return { recommendation, confidence: Math.round(confidence), reasons };
}

interface LightweightChartProps {
  symbol: string;
  height?: number;
}

export default function LightweightChart({ symbol, height = 500 }: LightweightChartProps) {
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<any>(null);
  const candlestickSeriesRef = useRef<any>(null);
  const volumeSeriesRef = useRef<any>(null);
  const smaSeriesRef = useRef<any>(null);
  const rsiSeriesRef = useRef<any>(null);
  const macdSeriesRef = useRef<any>(null);
  const macdHistogramRef = useRef<any>(null);
  const bbSeriesRef = useRef<any[]>([]);
  
  const [loading, setLoading] = useState(true);
  const [showSMA, setShowSMA] = useState(true);
  const [showBB, setShowBB] = useState(true);
  const [showRSI, setShowRSI] = useState(false);
  const [showMACD, setShowMACD] = useState(false);
  const [smaPeriod, setSmaPeriod] = useState(20);
  const [chartData, setChartData] = useState<ChartData[]>([]);
  const [aiAnalysis, setAiAnalysis] = useState<AIAnalysis | null>(null);
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);

  useEffect(() => {
    if (!chartContainerRef.current) return;

    const chart = createChart(chartContainerRef.current, {
      width: chartContainerRef.current.clientWidth,
      height,
      layout: {
        background: { color: '#1f2937' },
        textColor: '#9ca3af',
      },
      grid: {
        vertLines: { color: '#374151' },
        horzLines: { color: '#374151' },
      },
      crosshair: {
        mode: 1,
      },
      timeScale: {
        borderColor: '#4b5563',
        timeVisible: true,
      },
      rightPriceScale: {
        borderColor: '#4b5563',
      },
    });

    const candlestickSeries = chart.addSeries(CandlestickSeries, {
      upColor: '#22c55e',
      downColor: '#ef4444',
      borderUpColor: '#22c55e',
      borderDownColor: '#ef4444',
      wickUpColor: '#22c55e',
      wickDownColor: '#ef4444',
    });

    const volumeSeries = chart.addSeries(HistogramSeries, {
      color: '#3b82f6',
      priceFormat: { type: 'volume' },
      priceScaleId: '',
    });
    volumeSeries.priceScale().applyOptions({
      scaleMargins: { top: 0.8, bottom: 0 },
    });

    chartRef.current = chart;
    candlestickSeriesRef.current = candlestickSeries;
    volumeSeriesRef.current = volumeSeries;

    const handleResize = () => {
      if (chartContainerRef.current && chartRef.current) {
        chartRef.current.applyOptions({ width: chartContainerRef.current.clientWidth });
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      chart.remove();
    };
  }, [height]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      
      const ohlcvData = await fetchOHLCV(symbol, 180);
      
      if (ohlcvData.length === 0) {
        setLoading(false);
        return;
      }

      setChartData(ohlcvData);

      // Run AI analysis and scan opportunities
      const analysis = analyzeWithAI(ohlcvData);
      setAiAnalysis(analysis);
      
      const opp = scanOpportunities(ohlcvData);
      setOpportunities(opp);

      if (candlestickSeriesRef.current) {
        candlestickSeriesRef.current.setData(ohlcvData);
      }

      const volumeData: VolumeData[] = ohlcvData.map(d => ({
        time: d.time,
        value: Math.random() * 1000000,
        color: d.close >= d.open ? 'rgba(34, 197, 94, 0.5)' : 'rgba(239, 68, 68, 0.5)',
      }));
      
      if (volumeSeriesRef.current) {
        volumeSeriesRef.current.setData(volumeData);
      }

      if (showSMA) {
        const smaData = calculateSMA(ohlcvData, smaPeriod);
        if (smaSeriesRef.current) chartRef.current?.removeSeries(smaSeriesRef.current);
        const smaSeries = chartRef.current?.addSeries(LineSeries, {
          color: '#f59e0b', lineWidth: 2, priceLineVisible: false,
        });
        if (smaSeries) { smaSeries.setData(smaData); smaSeriesRef.current = smaSeries; }
      }

      if (showBB) {
        const bbData = calculateBB(ohlcvData, 20);
        bbSeriesRef.current.forEach(s => chartRef.current?.removeSeries(s));
        bbSeriesRef.current = [];
        
        if (bbData.length > 0) {
          const upper = chartRef.current?.addSeries(LineSeries, { color: '#8b5cf6', lineWidth: 1, lineStyle: 2, priceLineVisible: false });
          const middle = chartRef.current?.addSeries(LineSeries, { color: '#8b5cf6', lineWidth: 1, priceLineVisible: false });
          const lower = chartRef.current?.addSeries(LineSeries, { color: '#8b5cf6', lineWidth: 1, lineStyle: 2, priceLineVisible: false });
          
          if (upper && middle && lower) {
            upper.setData(bbData.map(d => ({ time: d.upper.time, value: d.upper.value })));
            middle.setData(bbData.map(d => ({ time: d.middle.time, value: d.middle.value })));
            lower.setData(bbData.map(d => ({ time: d.lower.time, value: d.lower.value })));
            bbSeriesRef.current = [upper, middle, lower];
          }
        }
      }

      chartRef.current?.timeScale().fitContent();
      setLoading(false);
    };

    loadData();
  }, [symbol, showSMA, showBB, smaPeriod]);

  return (
    <div>
      {/* AI Analysis Result */}
      {aiAnalysis && (
        <div className={`mb-4 p-4 rounded-lg border ${
          aiAnalysis.recommendation === 'BUY' ? 'bg-green-900/30 border-green-700' :
          aiAnalysis.recommendation === 'SELL' ? 'bg-red-900/30 border-red-700' :
          'bg-gray-800/50 border-gray-600'
        }`}>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-lg font-bold flex items-center gap-2">
              🤖 AI Analysis
              <span className={`px-2 py-0.5 rounded text-sm font-bold ${
                aiAnalysis.recommendation === 'BUY' ? 'bg-green-600 text-white' :
                aiAnalysis.recommendation === 'SELL' ? 'bg-red-600 text-white' :
                'bg-gray-600 text-white'
              }`}>
                {aiAnalysis.recommendation}
              </span>
              <span className="text-sm text-gray-400">{aiAnalysis.confidence}% confidence</span>
            </h3>
          </div>
          <div className="flex flex-wrap gap-2">
            {aiAnalysis.reasons.map((reason, idx) => (
              <span key={idx} className="text-sm bg-gray-800/50 px-2 py-1 rounded text-gray-300">{reason}</span>
            ))}
          </div>
        </div>
      )}

      {/* Opportunity Scanner */}
      <div className="mb-4">
        <h3 className="text-lg font-bold mb-3 flex items-center gap-2">
          🔍 Opportunity Scanner
          <span className="text-sm font-normal text-gray-400">({opportunities.length} signals)</span>
        </h3>
        
        {opportunities.length === 0 ? (
          <p className="text-gray-400 text-sm">No specific opportunities detected. Market may be in consolidation.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {opportunities.map((opp) => (
              <div
                key={opp.id}
                className={`p-3 rounded-lg border ${
                  opp.type === 'bullish' ? 'bg-green-900/20 border-green-700' :
                  opp.type === 'bearish' ? 'bg-red-900/20 border-red-700' :
                  'bg-gray-800/50 border-gray-600'
                }`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="font-medium flex items-center gap-1">
                    {opp.icon} {opp.name}
                  </span>
                  <span className={`text-xs px-1.5 py-0.5 rounded ${
                    opp.strength === 'strong' ? 'bg-yellow-600' :
                    opp.strength === 'moderate' ? 'bg-blue-600' : 'bg-gray-600'
                  }`}>
                    {opp.strength}
                  </span>
                </div>
                <p className="text-xs text-gray-400">{opp.description}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Controls */}
      <div className="flex flex-wrap gap-4 mb-4 p-3 bg-gray-800 rounded-lg">
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={showSMA} onChange={(e) => setShowSMA(e.target.checked)} className="w-4 h-4" />
          <span className="text-yellow-400 font-medium">SMA</span>
        </label>
        
        {showSMA && (
          <select value={smaPeriod} onChange={(e) => setSmaPeriod(Number(e.target.value))} className="bg-gray-700 text-white text-sm rounded px-2 py-1">
            <option value={9}>SMA 9</option>
            <option value={20}>SMA 20</option>
            <option value={50}>SMA 50</option>
            <option value={200}>SMA 200</option>
          </select>
        )}
        
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={showBB} onChange={(e) => setShowBB(e.target.checked)} className="w-4 h-4" />
          <span className="text-purple-400 font-medium">Bollinger Bands</span>
        </label>
        
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={showRSI} onChange={(e) => setShowRSI(e.target.checked)} className="w-4 h-4" />
          <span className="text-blue-400 font-medium">RSI</span>
        </label>
        
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={showMACD} onChange={(e) => setShowMACD(e.target.checked)} className="w-4 h-4" />
          <span className="text-cyan-400 font-medium">MACD</span>
        </label>
      </div>

      {/* Chart */}
      <div ref={chartContainerRef} className="w-full rounded-lg overflow-hidden" />
      
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-900/50">
          <p className="text-white">Loading chart data...</p>
        </div>
      )}
    </div>
  );
}